import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import ProtectedRoute from '@/components/ProtectedRoute';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import { lazy, Suspense } from 'react';
const AdminLayout = lazy(() => import('@/components/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminPosts = lazy(() => import('@/pages/admin/AdminPosts'));
const AdminProducts = lazy(() => import('@/pages/admin/AdminProducts'));
const AdminCRM = lazy(() => import('@/pages/admin/AdminCRM'));
const AdminReports = lazy(() => import('@/pages/admin/AdminReports'));
const AdminUploads = lazy(() => import('@/pages/admin/AdminUploads'));
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'));
const AdminNews = lazy(() => import('@/pages/admin/AdminNews'));
const AdminSocial = lazy(() => import('@/pages/admin/AdminSocial'));
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
import Layout from '@/components/site/Layout';
import Home from '@/pages/Home';
import About from '@/pages/About';
import Products from '@/pages/Products';
import ProductDetail from '@/pages/ProductDetail';
import Contact from '@/pages/Contact';
import FAQs from '@/pages/FAQs';
import NewsAndUpdates from '@/pages/NewsAndUpdates';
import NewsArticle from '@/pages/NewsArticle';

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faqs" element={<FAQs />} />
        <Route path="/news" element={<NewsAndUpdates />} />
        <Route path="/news/:slug" element={<NewsArticle />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
        <Route element={
          <Suspense fallback={
            <div className="fixed inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
            </div>
          }>
            <AdminLayout />
          </Suspense>
        }>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/posts" element={<AdminPosts />} />
          <Route path="/admin/products" element={<AdminProducts />} />
          <Route path="/admin/crm" element={<AdminCRM />} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="/admin/uploads" element={<AdminUploads />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/news" element={<AdminNews />} />
          <Route path="/admin/social" element={<AdminSocial />} />
        </Route>
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <ScrollToTop />
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App